import { ApiService } from "./ApiService.js";

/**
 * Indicator service for managing indicators, targets, actuals, and disaggregation
 */
export class IndicatorService extends ApiService {
  constructor() {
    super("/api/");
  }

  async createIndicator(data) {
    if (!data?.project) {
      throw new Error("Cannot create indicator: project ID is required");
    }
    try {
      const projectId = data.project;
      const response = await this.post(
        `projects/${projectId}/indicators/`,
        data
      );
      return response.data;
    } catch (error) {
      console.error("Failed to create indicator:", error);
      throw error;
    }
  }

  async getIndicators(params = {}) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value != null)
    );

    if (cleanParams.project_id) {
      const projectId = cleanParams.project_id;
      const { project_id, ...restParams } = cleanParams;
      const response = await this.get(`projects/${projectId}/indicators/`, {
        params: restParams,
      });
      return response.data;
    }

    const response = await this.get("indicators/", { params: cleanParams });
    return response.data;
  }

  async getDeletedIndicators(projectId, params = {}) {
    if (!projectId) {
      throw new Error("Cannot get deleted indicators: project ID is required");
    }
    const response = await this.get(
      `projects/${projectId}/indicators/deleted/`,
      { params }
    );
    return response.data;
  }

  async getIndicator(id) {
    if (!id) {
      throw new Error("Cannot get indicator: ID is required");
    }
    const response = await this.get(`indicators/${id}/`);
    return response.data;
  }

  async updateIndicator(id, data) {
    if (!id) {
      throw new Error("Cannot update indicator: ID is required");
    }
    if (!data) {
      throw new Error("Cannot update indicator: data is required");
    }
    try {
      const response = await this.patch(`indicators/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update indicator ${id}:`, error);
      throw error;
    }
  }

  async archiveIndicator(indicator) {
    if (!indicator?.id) {
      throw new Error("Cannot archive indicator: indicator object with ID is required");
    }
    try {
      const response = await this.patch(`indicators/${indicator.id}/`, {
        status: "archived",
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to archive indicator ${indicator.id}:`, error);
      throw error;
    }
  }

  async unarchiveIndicator(indicator) {
    if (!indicator?.id) {
      throw new Error("Cannot unarchive indicator: indicator object with ID is required");
    }
    try {
      const response = await this.patch(`indicators/${indicator.id}/`, {
        status: "active",
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to unarchive indicator ${indicator.id}:`, error);
      throw error;
    }
  }

  async deleteIndicator(id) {
    if (!id) {
      throw new Error("Cannot delete indicator: ID is required");
    }
    try {
      const response = await this.patch(`indicators/${id}/soft-delete/`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete indicator ${id}:`, error);
      throw error;
    }
  }

  async restoreIndicator(id) {
    if (!id) {
      throw new Error("Cannot restore indicator: ID is required");
    }
    try {
      const response = await this.patch(`indicators/${id}/restore/`);
      return response.data;
    } catch (error) {
      console.error(`Failed to restore indicator ${id}:`, error);
      throw error;
    }
  }

  async getCount(projectId) {
    if (!projectId) {
      throw new Error("Cannot get indicator count: project ID is required");
    }
    const response = await this.get(
      `projects/${projectId}/indicators/count/`
    );
    return response.data.count;
  }

  async getStats(projectId) {
    if (!projectId) {
      throw new Error("Cannot get indicator stats: project ID is required");
    }
    const response = await this.get(
      `projects/${projectId}/indicators/stats/`
    );
    return response.data;
  }

  async getIndicatorUnits(params = {}) {
    const response = await this.get("indicator-units/", {
      params: { active: true, ...params },
    });
    return response.data;
  }

  // --- Dimensions ---
  async getDimensions() {
    const response = await this.get("dimensions/");
    return response.data;
  }

  // --- Disaggregation ---
  async getDisaggregation(indicatorId) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.get(`indicators/${indicatorId}/disaggregation/`);
    return response.data;
  }

  async saveDisaggregation(indicatorId, data) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.post(`indicators/${indicatorId}/disaggregation/`, data);
    return response.data;
  }

  // --- Targets ---
  async getTargets(indicatorId) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.get(`indicators/${indicatorId}/targets/`);
    return response.data;
  }

  async configureTargets(indicatorId, data) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.post(`indicators/${indicatorId}/targets/configure/`, data);
    return response.data;
  }

  // --- Actuals ---
  async getActuals(indicatorId) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.get(`indicators/${indicatorId}/actuals/`);
    return response.data;
  }

  async saveActuals(indicatorId, data) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.put(`indicators/${indicatorId}/actuals/`, data);
    return response.data;
  }

  // --- Progress ---
  async getProgress(indicatorId) {
    if (!indicatorId) throw new Error("Indicator ID is required");
    const response = await this.get(`indicators/${indicatorId}/progress/`);
    return response.data;
  }
}

// Export service instance
export const indicatorService = new IndicatorService();

export default indicatorService;
